import pb from "../services/pocketbase";

/** PocketBase `users.sub_role_` select options (must match Admin schema). */
const SUB_ROLE_SELECT = new Set(["UX Designer", "UI Designer", "Front"]);

function normalizeEmailInput(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed ? trimmed.toLowerCase() : "";
}

function escapePbFilterValue(value) {
  // PocketBase filter strings use double quotes for string values.
  // Escape backslash and double quote defensively.
  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * Map one UI "role" value to either text field `sub_role` or select `sub_role_`.
 */
export function splitSubRoleForPocketBase(value) {
  if (value == null || String(value).trim() === "") return {};
  const s = String(value).trim();
  if (SUB_ROLE_SELECT.has(s)) {
    return { sub_role_: s };
  }
  return { sub_role: s };
}

/**
 * Legacy alpha backend sometimes stored `JSON.stringify(token)`.
 */
export function readStoredAuthToken() {
  let t = localStorage.getItem("token");
  if (t == null || t === "") return null;
  const trimmed = t.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      const parsed = JSON.parse(t);
      if (typeof parsed === "string" && parsed.length > 0) return parsed;
    } catch {
      /* keep raw */
    }
  }
  return t;
}

/**
 * Normalize API record for the UI (mediaLinks JSON, sub_role vs sub_role_, avatar/photo).
 */
export function normalizeUserRecord(record) {
  if (!record || typeof record !== "object") return record;
  const out = { ...record };

  let media = out.mediaLinks;
  if (media == null) media = [];
  else if (typeof media === "string") {
    try {
      media = JSON.parse(media);
    } catch {
      media = [];
    }
  }
  if (!Array.isArray(media)) media = [];
  out.mediaLinks = media;

  if (out.experience != null && typeof out.experience !== "number") {
    const n = Number(out.experience);
    out.experience = Number.isFinite(n) ? n : 0;
  }

  if (!out.avatar && out.photo) {
    out.avatar = out.photo;
  }

  if ((!out.sub_role || out.sub_role === "") && out.sub_role_) {
    out.sub_role = out.sub_role_;
  }

  return out;
}

async function urlToFile(url, filename = "avatar.jpg") {
  if (!url) return null;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch avatar image");
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

function dataUrlToFile(dataUrl, filename = "avatar.png") {
  if (!dataUrl) return null;
  try {
    const [header, base64] = dataUrl.split(",");
    const mimeMatch = header.match(/data:(.*?);base64/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new File([new Uint8Array(byteNumbers)], filename, { type: mime });
  } catch (e) {
    console.error("Failed to convert data URL to File", e);
    return null;
  }
}

/**
 * Password login. `identity` may be email or username (trimmed); both must be enabled as identity fields on the `users` collection in PocketBase Admin.
 */
export async function login(identity, password) {
  await pb.collection("users").authWithPassword(
    typeof identity === "string" ? identity.trim() : identity,
    password
  );
  localStorage.setItem("token", pb.authStore.token);
  return {
    token: pb.authStore.token,
    record: pb.authStore.model,
  };
}

export async function logout() {
  pb.authStore.clear();
  localStorage.removeItem("token");
}

/**
 * Start email change from Settings. PocketBase emails a confirmation link to the new address;
 * the change applies after the user opens that link (built-in auth flow).
 */
export async function requestUserEmailChange(newEmail) {
  const e = typeof newEmail === "string" ? newEmail.trim() : "";
  if (!e) throw new Error("Email is required");
  await pb.collection("users").requestEmailChange(e);
}

/**
 * Re-verify password, then request PocketBase email change (fallback when legacy API is unavailable).
 */
export async function verifyPasswordAndRequestEmailChange(password, newEmail) {
  const user = currentUser();
  if (!user?.id) throw new Error("Not authenticated");
  const identity =
    (user.email && String(user.email).trim()) || user.username || "";
  if (!identity) throw new Error("Missing login identity");
  await pb.collection("users").authWithPassword(identity, password);
  if (pb.authStore.token) {
    localStorage.setItem("token", pb.authStore.token);
  }
  await requestUserEmailChange(newEmail);
}

/**
 * Best-effort check whether the email already exists in PocketBase.
 * Note: This may fail due to collection rules (403). Callers should treat errors as "unknown"
 * and proceed with the normal requestEmailChange flow (which will also validate uniqueness).
 */
export async function isEmailRegistered(email) {
  const e = normalizeEmailInput(email);
  if (!e) throw new Error("Email is required");
  try {
    await pb
      .collection("users")
      .getFirstListItem(`email="${escapePbFilterValue(e)}"`);
    return true;
  } catch (err) {
    const status = err?.status ?? 0;
    if (status === 404) return false;
    // PocketBase sometimes throws 400 with "no rows in result set"/"no record found"
    const msg = String(err?.message || "");
    if (status === 400 && msg.toLowerCase().includes("no")) return false;
    throw err;
  }
}

export async function signup(data, avatarUrl) {
  const avatarFile = avatarUrl ? await urlToFile(avatarUrl, "avatar.jpg") : null;

  const formData = new FormData();
  formData.append("email", data.email);
  formData.append("password", data.password);
  formData.append("passwordConfirm", data.password);

  const rawUser = (data.username || data.email.split("@")[0]).trim().slice(0, 16);
  formData.append("username", rawUser);

  if (data.fullName) formData.append("fullName", data.fullName);
  if (data.nationality) formData.append("nationality", data.nationality);
  if (data.intro) formData.append("intro", data.intro);

  const roleSplit = splitSubRoleForPocketBase(data.sub_role);
  if (roleSplit.sub_role) formData.append("sub_role", roleSplit.sub_role);
  if (roleSplit.sub_role_) formData.append("sub_role_", roleSplit.sub_role_);

  if (data.experience != null && data.experience !== "")
    formData.append("experience", String(data.experience));
  if (data.personalSite) formData.append("personalSite", data.personalSite);
  if (data.personalBlog) formData.append("personalBlog", data.personalBlog);
  if (Array.isArray(data.mediaLinks) && data.mediaLinks.length) {
    formData.append("mediaLinks", JSON.stringify(data.mediaLinks));
  }
  if (typeof data.isPublic === "boolean") formData.append("isPublic", data.isPublic);
  if (typeof data.allowSendEmail === "boolean")
    formData.append("allowSendEmail", data.allowSendEmail);
  if (typeof data.receiveNews === "boolean")
    formData.append("receiveNews", data.receiveNews);
  if (data.uxerIcon) formData.append("uxerIcon", data.uxerIcon);

  if (data.role) formData.append("role", data.role);
  if (data.roleStatus) formData.append("roleStatus", data.roleStatus);

  if (avatarFile) formData.append("avatar", avatarFile);

  const created = await pb.collection("users").create(formData);
  await login(data.email, data.password);
  return created;
}

export async function requestPasswordReset(email) {
  return pb.collection("users").requestPasswordReset(email);
}

export async function confirmPasswordReset(token, newPassword) {
  await pb.collection("users").confirmPasswordReset(token, newPassword, newPassword);
  if (pb.authStore.token) {
    localStorage.setItem("token", pb.authStore.token);
  }
  return pb.authStore.model;
}

export function currentUser() {
  return pb.authStore.model;
}

export function isAuthenticated() {
  return pb.authStore.isValid;
}

export function getFileUrl(record, filename) {
  if (!record) return null;
  let fname = filename;
  if (fname == null || fname === "") {
    const a = record.avatar ?? record.photo;
    if (Array.isArray(a)) fname = a[0];
    else if (typeof a === "string") fname = a;
  }
  if (!fname) return null;
  return pb.files.getUrl(record, fname);
}

export async function ensureAuthHydrated() {
  try {
    if (pb.authStore.isValid && pb.authStore.model?.id) {
      return normalizeUserRecord(pb.authStore.model);
    }

    const legacyToken = readStoredAuthToken();
    if (!pb.authStore.token && legacyToken) {
      pb.authStore.save(legacyToken, null);
    }

    if (!pb.authStore.token) return null;

    if (pb.authStore.isValid && pb.authStore.model?.id) {
      return normalizeUserRecord(pb.authStore.model);
    }

    try {
      const auth = await pb.collection("users").authRefresh();
      localStorage.setItem("token", pb.authStore.token);
      const rec = auth?.record ?? pb.authStore.model;
      return rec ? normalizeUserRecord(rec) : null;
    } catch (refreshErr) {
      const status = refreshErr?.status ?? 0;
      if (status === 401 || status === 403) {
        pb.authStore.clear();
        localStorage.removeItem("token");
        return null;
      }
      console.warn("ensureAuthHydrated authRefresh failed (non-fatal)", refreshErr);
      if (pb.authStore.model?.id) {
        return normalizeUserRecord(pb.authStore.model);
      }
      return null;
    }
  } catch (e) {
    console.warn("ensureAuthHydrated failed", e);
    return null;
  }
}

export async function refreshCurrentUser() {
  const user = pb.authStore.model;
  if (!user) return null;
  const me = await pb.collection("users").getOne(user.id);
  pb.authStore.save(pb.authStore.token, me);
  return normalizeUserRecord(me);
}

export async function updateProfile(updates = {}) {
  const user = currentUser();
  if (!user) throw new Error("Not authenticated");

  const formData = new FormData();

  const basicFields = [
    "username",
    "fullName",
    "nationality",
    "intro",
    "personalSite",
    "personalBlog",
  ];
  basicFields.forEach((key) => {
    if (updates[key] !== undefined && updates[key] !== null) {
      formData.append(key, updates[key]);
    }
  });

  if (updates.sub_role !== undefined && updates.sub_role !== null) {
    formData.append("sub_role", updates.sub_role);
  }
  if (updates.sub_role_ !== undefined && updates.sub_role_ !== null) {
    formData.append("sub_role_", updates.sub_role_);
  }

  if (updates.experience !== undefined && updates.experience !== null) {
    formData.append("experience", String(Number(updates.experience) || 0));
  }

  let mediaLinks = updates.mediaLinks;
  if (!Array.isArray(mediaLinks)) {
    const known = [
      "facebook",
      "instagram",
      "linkedin",
      "dribbble",
      "behance",
      "threads",
      "youtube",
      "figma",
      "tiktok",
      "x",
    ];
    mediaLinks = known
      .map((type) => ({ type, link: updates[type] }))
      .filter((l) => l.link);
  }
  if (mediaLinks && mediaLinks.length) {
    formData.append("mediaLinks", JSON.stringify(mediaLinks));
  }

  if (updates.profile_img) {
    let avatarFile = null;
    try {
      if (typeof updates.profile_img === "string" && updates.profile_img.startsWith("data:")) {
        avatarFile = dataUrlToFile(updates.profile_img, "avatar.png");
      } else if (typeof updates.profile_img === "string") {
        avatarFile = await urlToFile(updates.profile_img, "avatar.jpg");
      }
    } catch (e) {
      console.warn("Avatar conversion failed", e);
    }
    if (avatarFile) formData.append("avatar", avatarFile);
  }

  const updated = await pb.collection("users").update(user.id, formData);
  await refreshCurrentUser();
  return updated;
}

export async function updateProfileFields(updates = {}) {
  const user = currentUser();
  if (!user) throw new Error("Not authenticated");

  const payload = {};

  ["username", "fullName", "nationality", "intro", "personalSite", "personalBlog"].forEach(
    (key) => {
      if (updates[key] !== undefined && updates[key] !== null) {
        payload[key] = updates[key];
      }
    }
  );

  if (updates.sub_role !== undefined && updates.sub_role !== null && updates.sub_role !== "") {
    payload.sub_role = updates.sub_role;
  }
  if (updates.sub_role_ !== undefined && updates.sub_role_ !== null && updates.sub_role_ !== "") {
    payload.sub_role_ = updates.sub_role_;
  }

  if (updates.experience !== undefined && updates.experience !== null) {
    payload.experience = Number(updates.experience) || 0;
  }

  let mediaLinks = updates.mediaLinks;
  if (!Array.isArray(mediaLinks)) {
    const known = [
      "facebook",
      "instagram",
      "linkedin",
      "dribbble",
      "behance",
      "threads",
      "youtube",
      "figma",
      "tiktok",
      "x",
    ];
    mediaLinks = known
      .map((type) => ({ type, link: updates[type] }))
      .filter((l) => l.link);
  }
  if (mediaLinks && mediaLinks.length) {
    payload.mediaLinks = mediaLinks;
  }

  ["isPublic", "allowSendEmail", "receiveNews"].forEach((key) => {
    if (typeof updates[key] === "boolean") {
      payload[key] = updates[key];
    }
  });

  const updated = await pb.collection("users").update(user.id, payload);
  await refreshCurrentUser();
  return updated;
}

export async function changePasswordPB(oldPassword, newPassword) {
  const user = currentUser();
  if (!user) throw new Error("Not authenticated");

  const payload = {
    oldPassword,
    password: newPassword,
    passwordConfirm: newPassword,
  };

  const updated = await pb.collection("users").update(user.id, payload);
  pb.authStore.save(pb.authStore.token, updated);
  return updated;
}
